// activityReactions.js

import { EngagementType } from 'utilities/Enums';
import { getPersonName } from 'utilities/PersonIdentity';
import {
  isHomeAuthoringNoise,
  isHomeChecklistFieldSummary,
} from 'home/activity/utils/homeActivityFilters';

export const ACTIVITY_REACTION_DEFAULT = 'thumbs_up';

export const ACTIVITY_REACTION_TYPES = Object.freeze([
  { key: 'thumbs_up', glyph: '👍', label: 'Thumbs up' },
  { key: 'clap', glyph: '👏', label: 'Clap' },
  { key: 'heart', glyph: '❤️', label: 'Heart' },
  { key: 'laugh', glyph: '😂', label: 'Laugh' },
  { key: 'shocked', glyph: '😮', label: 'Shocked' },
]);

const ACTIVITY_REACTION_KEY_SET = new Set(
  ACTIVITY_REACTION_TYPES.map((entry) => entry.key)
);

const REOPEN_CHANGE_KINDS = new Set(['item_reopened']);

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeActivityReactionKey(value) {
  const key = String(value || '').trim();
  return ACTIVITY_REACTION_KEY_SET.has(key) ? key : '';
}

/**
 * @param {object|null|undefined} event
 * @returns {string}
 */
export function getActivityEventKey(event) {
  return String(event?.key || event?.id || '').trim();
}

/**
 * @param {object|null|undefined} comment
 * @returns {string}
 */
export function getActivityCommentKey(comment) {
  return String(comment?.key || comment?.id || '').trim();
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getActivityReactionCommentKey(row) {
  return String(row?.commentKey || '').trim();
}

/**
 * Actor to notify (the person who did the work).
 *
 * @param {object|null|undefined} event
 * @param {object|null|undefined} [node]
 * @returns {string}
 */
export function getActivityReactionTargetKey(event, node = null) {
  return String(
    event?.actorKey
    || event?.actorUserKey
    || event?.user?.key
    || event?.user?.userKey
    || node?.userKey
    || ''
  ).trim();
}

/**
 * Comment author to notify.
 *
 * @param {object|null|undefined} comment
 * @param {object|null|undefined} [node]
 * @returns {string}
 */
export function getActivityCommentTargetKey(comment, node = null) {
  return String(
    comment?.userKey
    || comment?.user?.key
    || comment?.user?.userKey
    || node?.userKey
    || ''
  ).trim();
}

/**
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function canReactToActivityEvent(event) {
  if (!event) return false;
  if (isHomeAuthoringNoise(event)) return false;
  if (isHomeChecklistFieldSummary(event)) return false;

  const kinds = (Array.isArray(event.changes) ? event.changes : [])
    .map((change) => String(change?.changeKind || '').trim())
    .filter(Boolean);
  if (kinds.some((kind) => REOPEN_CHANGE_KINDS.has(kind))) return false;

  return Boolean(getActivityEventKey(event));
}

/**
 * @param {object|null|undefined} comment
 * @returns {boolean}
 */
export function canReactToActivityComment(comment) {
  if (!comment || comment.deleted === true) return false;
  return Boolean(getActivityCommentKey(comment));
}

/**
 * Dual-read: ACTIVITY_REACTION keeps its type; legacy COMMENT_LIKE is heart.
 * COMMENT_DISLIKE is hidden.
 *
 * @param {object|null|undefined} row
 * @returns {object|null}
 */
export function normalizeActivityReactionRow(row) {
  if (!row) return null;
  const type = String(row.type || '').trim();
  if (type === EngagementType.COMMENT_DISLIKE) return null;
  if (type === EngagementType.COMMENT_LIKE) {
    return { ...row, reaction: 'heart' };
  }
  if (type && type !== EngagementType.ACTIVITY_REACTION) return null;
  const reaction = normalizeActivityReactionKey(row.reaction);
  if (!reaction) return null;
  return { ...row, reaction };
}

/**
 * One reaction per person: ACTIVITY_REACTION wins over a leftover COMMENT_LIKE.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function collapseActivityReactionRows(rows) {
  const byUser = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const normalized = normalizeActivityReactionRow(row);
    if (!normalized) return;
    const userKey = String(normalized.userKey || '').trim();
    if (!userKey) return;
    const previous = byUser.get(userKey);
    if (!previous) {
      byUser.set(userKey, normalized);
      return;
    }
    const previousIsLegacy = previous.type === EngagementType.COMMENT_LIKE;
    const nextIsCurrent = normalized.type === EngagementType.ACTIVITY_REACTION;
    if (previousIsLegacy && nextIsCurrent) {
      byUser.set(userKey, normalized);
    }
  });
  return Array.from(byUser.values());
}

/**
 * The viewer's current reaction doc, plus any leftover COMMENT_LIKE to retire.
 *
 * @param {object[]} rows
 * @param {string} [userKey]
 * @returns {{ existing: object|null, legacyLike: object|null }}
 */
export function resolveMyActivityReaction(rows, userKey = '') {
  const viewerKey = String(userKey || '').trim();
  let existing = null;
  let legacyLike = null;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!viewerKey || String(row?.userKey || '').trim() !== viewerKey) return;
    if (row?.type === EngagementType.COMMENT_LIKE) {
      legacyLike = row;
      if (!existing) existing = row;
      return;
    }
    if (row?.type === EngagementType.ACTIVITY_REACTION || normalizeActivityReactionKey(row?.reaction)) {
      existing = row;
    }
  });
  return { existing, legacyLike };
}

/**
 * @param {object[]} rows
 * @returns {Map<string, object[]>}
 */
export function indexActivityReactionsByEventKey(rows) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const eventKey = String(row?.eventKey || '').trim();
    if (!eventKey) return;
    const list = map.get(eventKey) || [];
    list.push(row);
    map.set(eventKey, list);
  });
  return map;
}

/**
 * @param {object[]} rows
 * @returns {Map<string, object[]>}
 */
export function indexActivityReactionsByCommentKey(rows) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const commentKey = getActivityReactionCommentKey(row);
    if (!commentKey) return;
    if (String(row?.type || '').trim() === EngagementType.COMMENT_DISLIKE) return;
    const list = map.get(commentKey) || [];
    list.push(row);
    map.set(commentKey, list);
  });
  return map;
}

/**
 * @param {object[]} rows
 * @param {{ userKey?: string }} [options]
 * @returns {{
 *   chips: Array<{
 *     key: string,
 *     glyph: string,
 *     count: number,
 *     names: string[],
 *     mine: boolean,
 *   }>,
 *   mine: string,
 * }}
 */
export function aggregateActivityReactions(rows, { userKey = '' } = {}) {
  const viewerKey = String(userKey || '').trim();
  const namesByType = new Map();
  let mine = '';

  collapseActivityReactionRows(rows).forEach((row) => {
    const typeKey = normalizeActivityReactionKey(row?.reaction);
    if (!typeKey) return;
    const list = namesByType.get(typeKey) || [];
    const name = getPersonName(row?.user, '') || 'Someone';
    list.push(name);
    namesByType.set(typeKey, list);
    if (viewerKey && String(row?.userKey || '').trim() === viewerKey) {
      mine = typeKey;
    }
  });

  const chips = ACTIVITY_REACTION_TYPES.flatMap((entry) => {
    const names = namesByType.get(entry.key) || [];
    if (names.length === 0) return [];
    return [{
      key: entry.key,
      glyph: entry.glyph,
      count: names.length,
      names,
      mine: mine === entry.key,
    }];
  });

  return { chips, mine };
}

/**
 * @param {string} actorName
 * @param {string} reaction
 * @returns {string}
 */
export function formatActivityReactionNotificationTitle(actorName, reaction) {
  const name = String(actorName || 'Someone').trim() || 'Someone';
  switch (normalizeActivityReactionKey(reaction)) {
    case 'clap':
      return `${name} clapped for your update`;
    case 'heart':
      return `${name} loved your update`;
    case 'laugh':
      return `${name} laughed at your update`;
    case 'shocked':
      return `${name} was surprised by your update`;
    case 'thumbs_up':
    default:
      return `${name} gave a thumbs up on your update`;
  }
}
